import * as React from 'react';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { ToastContext, ToastContextType } from '../../util/providers/ToastProvider';
import { IntlShape, useIntl } from 'react-intl';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router';
import { AuthContext } from '../../Auth/components/AuthProvider';
import {
  ApiResponse,
  EmploymentPeriodDTO,
  ExternalIdDTO,
  PersonApi,
  PersonDTO,
  PersonEmailDTO,
  PersonInstitutionalIdDTO,
  PersonNameDTO,
  PersonStatusDTO,
  PersonToOrganisationDTO,
  ReferencedProjectDTO,
  UrlDTO,
} from '../../api/core';
import { getAuthMiddleware } from '../../Auth/utils/GetAuthMiddleware';
import { Loading } from '../system/Loading';
import { Row } from './components/layout/Row';
import { renderDetailAccordionTab } from './components/layout/renderDetailAccordionTab';
import View from './components/layout/View';
import { Button } from 'primereact/button';
import { BreakingList } from './components/layout/BreakingList';
import { ExternalId } from './components/ExternalId';
import { InstitutionalId } from './components/InstitutionalId';
import SearchResultsWork from '../dashboard/SearchResultsWork';
import { deletePerson, requestDeletePerson } from '../../util/actions/person-actions';
import SearchResultsOrganisation from '../dashboard/SearchResultsOrganisation';
import SearchResultsProject from '../dashboard/SearchResultsProject';
import { getNotation } from '../../Vocabulary/util';
import { ExternalLink } from '../components/ExternalLink';
import { boolFilter } from '../../Vocabulary/data';
import { localDate, localDateTime } from '../../util/intl.utils';
import { OrcidMethod, orcidRequest, OrcidResult } from '../../util/orcid.utils';
import { ButtonGroup } from 'primereact/buttongroup';
import { InternalLink } from '../components/InternalLink';
import { Stamp } from '../components/Stamp';
import { UserAvatarComponent } from '../components/UserAvatar';
import { UrlRender, UrlsRow } from './components/UrlsRow';
import { SubjectsRow } from './components/SubjectsRow';

export const DetailPerson = (): ReactNode => {
  const toast: ToastContextType = useContext(ToastContext);
  const intl: IntlShape = useIntl();
  const navigate: NavigateFunction = useNavigate();
  const params: Readonly<Params<string>> = useParams();
  const {actions, authorizer} = useContext(AuthContext);
  const personApi: PersonApi = new PersonApi().withMiddleware(getAuthMiddleware(actions));

  const [person, setPerson] = useState<PersonDTO>();


  useEffect(() => {
    if (params.id === undefined) {
      return void navigate('/search/persons');
    }
    const id: number = Number(params.id);
    if (isNaN(id)) {
    toast.notify({ severity: 'error', summary: intl.formatMessage({ id: 'message.loading.failed.message' }) });
      return void navigate('/error/404');
    }
    if (!person || id !== person.id) {
      window.scrollTo(0, 0);
      void fetchData(id);
    }
  }, [params.id]);

  const fetchData = async (personId: number): Promise<void> => {
    try {
      const response: ApiResponse<PersonDTO> = await personApi.getPersonRaw({personId});
      if (!response.raw.ok) {
        toast.notify({severity: 'error', summary: intl.formatMessage({id: 'message.loading.failed.message'})});
        return navigate(`/error/${response.raw.status}`);
      }
      setPerson(await response.value());
    } catch (error) {
      toast.notify({severity: 'error', summary: intl.formatMessage({id: 'message.loading.failed.message'})});
      return void navigate(`/error/${error?.status || 500}`);
    }
  };

  const orcidAction = async (method: OrcidMethod) => {
    const orcidId: string = person.externalIds?.find((externalId: ExternalIdDTO): boolean => externalId.type === 'orcid')?.externalId;
    const result: OrcidResult = await orcidRequest(orcidId, method, actions);
    if (result.type === 'success') {
      toast.notify({
        severity: 'success',
        summary: intl.formatMessage({id: result.method === 'pull' ? 'popup.orcid.pull.success.title' : 'popup.orcid.push.success.title'}),
        detail:
          <div>
            <p>{intl.formatMessage({id: result.method === 'pull' ? 'popup.orcid.pull.success.message' : 'popup.orcid.push.success.message'})}</p>
            <p>{result.message}</p>
          </div>,
      });
    } else {
      toast.notify({
        severity: 'error',
        summary: intl.formatMessage({id: result.method === 'pull' ? 'popup.orcid.pull.error.title' : 'popup.orcid.push.error.title'}),
        detail:
          <div>
            <p>{intl.formatMessage({id: result.method === 'pull' ? 'popup.orcid.pull.error.message' : 'popup.orcid.push.error.message'})}</p>
            <p>{result.message}</p>
          </div>,
      });
    }
  };

  if (!person) {
    return <Loading />;
  }

  const getMainName = (personPTO: PersonDTO): PersonNameDTO => {
    return personPTO.personNames.find((name: PersonNameDTO): boolean => name.isMain);
  };

  const displayName = (name: PersonNameDTO): string => {
    return `${name.givenname} ${name.surname}`;
  };

  // RENDER DATA FOR HEADER

  const renderName = (personName: PersonNameDTO): ReactNode => (
    <span className={'flex gap-1'}>
      {personName.salutation ? (
        <span>{getNotation('salutation_list', personName.salutation, intl.locale)} </span>
      ) : (
        <></>
      )}
      {`${personName.givenname ?? ''} ${personName.surname ?? ''}`.trim()}
    </span>
  );

  const renderNames = (personNames: PersonNameDTO[]): ReactNode => {
    if (personNames.length < 2) return <></>;
    return (
      <div className={'flex flex-column md:flex-row gap-1'}>
        {`${intl.formatMessage({id: 'person.attr.personNames.label'})}:`}
        <BreakingList separator={','}>
          {personNames.map((personName: PersonNameDTO, index: number): ReactNode =>
            <div key={index}>{`${personName.givenname} ${personName.surname}`}</div>,
          )}
        </BreakingList>
      </div>
    );
  };

  const renderExternalIds = (personDTO: PersonDTO): ReactNode => {
    if (!personDTO.externalIds || !personDTO.externalIds.length) return <></>;
    return <BreakingList separator={''}>
      {personDTO.externalIds.map((externalIdDTO: ExternalIdDTO, index: number): ReactNode =>
        <div key={index}><ExternalId id={externalIdDTO} entity={'person'}/></div>,
      )}
    </BreakingList>;
  };

  const renderInstitutionalIds = (personDTO: PersonDTO): ReactNode => {
    if (!personDTO.personInstitutionalIds || !personDTO.personInstitutionalIds.length) return <></>;
    return <BreakingList>
      {personDTO.personInstitutionalIds.map((institutionalId: PersonInstitutionalIdDTO, index: number): ReactNode =>
        <div key={index}><InstitutionalId id={institutionalId} entity={'person'}/></div>,
      )}
    </BreakingList>;
  };

  const renderPersonStatus = (personDTO: PersonDTO): ReactNode => {
    if (!personDTO.personStatuses || !personDTO.personStatuses.length) return <></>;
    return <div className={'flex flex-column md:flex-row gap-1'}>
      {`${intl.formatMessage({id: 'person.attr.personStatuses.attr.status.label'})}:`}
      <BreakingList separator={','}>
        {personDTO.personStatuses.map((status: PersonStatusDTO, idx: number): ReactNode => {
          return <div
            key={idx}>{getNotation('person_status_list', status.status, intl.locale, status.affiliation)} ({getNotation('affiliations_list', status.affiliation, intl.locale)})</div>;
        })}
      </BreakingList>
    </div>;
  };


  // RENDER DATA FOR DETAILS

  const renderBasicData = (personDTO: PersonDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (personDTO.personNames && personDTO.personNames.length) {
      rows.push(<Row title={intl.formatMessage({id: 'person.attr.personNames.label'})}>
        {personDTO.personNames.map((name: PersonNameDTO, index: number): ReactNode =>
          <div key={index}>{renderName(name)}</div>,
        )}
      </Row>);
    }
    if (personDTO.personStatuses && personDTO.personStatuses.length) {
      rows.push(<Row title={intl.formatMessage({id: 'person.attr.personStatuses.attr.status.label'})}>
        {personDTO.personStatuses.map((status: PersonStatusDTO, index: number): ReactNode =>
          <div
            key={index}>{getNotation('person_status_list', status.status, intl.locale, status.affiliation)} ({getNotation('affiliations_list', status.affiliation, intl.locale)})</div>,
        )}
      </Row>);
    }
    if (personDTO.urls && personDTO.urls.length) {
      rows.push(
        <UrlsRow
          title={intl.formatMessage({ id: 'person.attr.urls.label' })}
          urls={(personDTO.urls || []).map(
            (url: UrlDTO): UrlRender => ({ url: url.url, type: getNotation('url_list', url.type, intl.locale) })
          )}
        />
      );
    }
    if (personDTO.personEmails && personDTO.personEmails.length) {
      rows.push(<Row title={intl.formatMessage({id: 'person.attr.personEmails.label'})}>
        {personDTO.personEmails
          .sort((mail1: PersonEmailDTO, mail2: PersonEmailDTO): number => {
            if (mail1.type === 'campus') return -1;
            if (mail2.type === 'campus') return 1;
            return mail1.type.localeCompare(mail2.type);
          })
          .map((mail: PersonEmailDTO, index: number): ReactNode =>
            <div className={'flex gap-2'} key={index}>
              <ExternalLink href={`mailto:${mail.email}`}>{mail.email}</ExternalLink>
              ({getNotation('email_types_list', mail.type, intl.locale)})
            </div>,
          )
        }
      </Row>);
    }
    if (personDTO.note && personDTO.note.length) {
      rows.push(<Row title={intl.formatMessage({id: 'person.attr.note.label'})}>{personDTO.note}</Row>);
    }
    return renderDetailAccordionTab(intl, rows, 'person.header.basic');
  };

  const renderAdditionalInformationData = (personDTO: PersonDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (personDTO.personSubjects && personDTO.personSubjects.length > 0) {
      rows.push(<SubjectsRow subjects={personDTO.personSubjects}/>);
    }
    return renderDetailAccordionTab(intl, rows, 'person.header.additionalInformation');
  }

  const renderIdsData = (personDTO: PersonDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (personDTO.externalIds && personDTO.externalIds.length) {
      rows.push(<Row title={intl.formatMessage({id: 'person.attr.externalIds.label'})}>
        {personDTO.externalIds.map((externalId: ExternalIdDTO, index: number): ReactNode =>
          <div key={index}><ExternalId id={externalId} entity={'person'}/></div>,
        )}
      </Row>);
    }
    if (personDTO.personInstitutionalIds && personDTO.personInstitutionalIds.length) {
      rows.push(<Row title={intl.formatMessage({id: 'person.attr.institutionalIds.label'})}>
        {personDTO.personInstitutionalIds.map((institutionalId: PersonInstitutionalIdDTO, index: number): ReactNode =>
          <div key={index}><InstitutionalId id={institutionalId} entity={'person'}/></div>,
        )}
      </Row>);
    }
    return renderDetailAccordionTab(intl, rows, 'person.header.ids');
  };

  const renderLinkingData = (personDTO: PersonDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (personDTO.personOrganisations && personDTO.personOrganisations.length) {
      rows.push(<Row title={intl.formatMessage({id: 'person.attr.personOrganisations.label'})}>
        {personDTO.personOrganisations.map((organisation: PersonToOrganisationDTO, index: number): ReactNode =>
          <div key={index} className={'flex flex-column'}>
            <div className={'flex flex-row justify-content-between'}>
              <div>
                <InternalLink
                  to={`/organisation/${organisation.organisation.id}`}>{organisation.organisation.name}</InternalLink>
              </div>
              <div><small className={'white-space-nowrap'}>{`ID: ${organisation.organisation.id}`}</small></div>
            </div>
            {organisation.employmentPeriods && organisation.employmentPeriods.length &&
              organisation.employmentPeriods.map((employmentPeriod: EmploymentPeriodDTO, eid: number): ReactNode =>
                <div
                  key={eid}>{localDate(employmentPeriod.startDate, intl.locale)} - {localDate(employmentPeriod.endDate, intl.locale)}</div>,
              )}
          </div>,
        )}
      </Row>);
    }
    if (personDTO.projects && personDTO.projects.length) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'person.attr.projects.label' })}>
          {(personDTO.projects || []).map(
            (project: ReferencedProjectDTO, index: number): ReactNode => (
              <div key={index} className={'flex flex-column'}>
                <div className={'flex flex-row justify-content-between'}>
                  <div>
                    <InternalLink to={`/project/${project.id}`}>{project.name}</InternalLink>
                  </div>
                  <div>
                    <small className={'white-space-nowrap'}>{`ID: ${project.id}`}</small>
                  </div>
                </div>
              </div>
            )
          )}
        </Row>
      );
    }
    return renderDetailAccordionTab(intl, rows, 'person.header.linking');
  };

  const renderAdminData = (personDTO: PersonDTO): ReactNode => {
    const rows: ReactNode[] = [];
    rows.push(<Row title={intl.formatMessage({id: 'person.attr.id.label'})}>{personDTO.id || ''}</Row>);
    if (personDTO.adminDataPerson.changed) {
      rows.push(<Row
        title={intl.formatMessage({id: 'person.attr.changed.label'})}>{localDateTime(personDTO.adminDataPerson.changed, intl.locale)}</Row>);
    }
    if (personDTO.adminDataPerson.created) {
      rows.push(<Row
        title={intl.formatMessage({id: 'person.attr.created.label'})}>{localDateTime(personDTO.adminDataPerson.created, intl.locale)}</Row>);
    }
    if (personDTO.adminDataPerson.origin) {
      rows.push(<Row
        title={intl.formatMessage({id: 'person.attr.origin.label'})}>{personDTO.adminDataPerson.origin}</Row>);
    }
    if (personDTO.adminDataPerson.deskman) {
      rows.push(<Row
        title={intl.formatMessage({id: 'person.attr.deskman.label'})}>{personDTO.adminDataPerson.deskman}</Row>);
    }
    if(personDTO.adminDataPerson.hidden) {
      rows.push(<Row
        title={intl.formatMessage({id: 'person.attr.hidden.label'})}>{boolFilter(personDTO.adminDataPerson.hidden || false)}</Row>);
    }
    if (personDTO.adminDataPerson.dataLastSupplied) {
      rows.push(<Row
        title={intl.formatMessage({id: 'person.attr.lastSupplied.label'})}>{localDate(personDTO.adminDataPerson.dataLastSupplied, intl.locale)}</Row>);
    }
    if (personDTO.adminDataPerson.personChecked) {
      rows.push(<Row
        title={intl.formatMessage({id: 'person.attr.personChecked.label'})}>{localDate(personDTO.adminDataPerson.personChecked, intl.locale)}</Row>);
    }
    if (personDTO.adminDataPerson.editorialStatus) {
      rows.push(<Row title={intl.formatMessage({id: 'person.attr.editorialStatus.label'})}>
        {getNotation('editorial_status_list', personDTO.adminDataPerson.editorialStatus, intl.locale)}
      </Row>);
    }
    return renderDetailAccordionTab(intl, rows, 'person.header.administration');
  };

  return (
    <View
      title={renderName(
        person.personNames.find((personName: PersonNameDTO): boolean => personName.isMain) || person.personNames[0]
      )}
      header={
        <div className={'flex justify-content-between'}>
          <div>
            {renderNames(person.personNames)}
            {renderExternalIds(person)}
            {renderInstitutionalIds(person)}
            {renderPersonStatus(person)}
            {person.adminDataPerson?.hidden && intl.formatMessage({ id: 'person.attr.adminDataPerson.attr.hidden' })}
          </div>
          <div>
            <div className={'hidden sm:block h-10rem w-10rem'}>
              <UserAvatarComponent person={person} type={'large'} />
            </div>
          </div>
        </div>
      }
      toolbar={{
        left: [],
        center: [
          authorizer.hasRoleOrHigher('ROLE_EDITOR') && person.adminDataPerson.hidden && (
            <Stamp red={true}>{intl.formatMessage({ id: 'person.attr.hidden.label' })}</Stamp>
          ),
        ],
        right: [
          authorizer.hasRole('ROLE_ADMIN') &&
            person.externalIds?.find((externalId: ExternalIdDTO): boolean => externalId.type === 'orcid')
              ?.externalId && (
              <ButtonGroup>
                <Button
                  label={intl.formatMessage({ id: 'button.orcid.push' })}
                  severity={'secondary'}
                  onClick={(): Promise<void> => orcidAction('push')}
                  title={intl.formatMessage({ id: 'button.orcid.push' })}
                />
                <Button
                  label={intl.formatMessage({ id: 'button.orcid.pull' })}
                  severity={'secondary'}
                  onClick={(): Promise<void> => orcidAction('pull')}
                  title={intl.formatMessage({ id: 'button.orcid.pull' })}
                />
              </ButtonGroup>
            ),
          authorizer.hasRoleOrHigher('ROLE_EDITOR') && (
            <Button
              icon={'pi pi-pencil'}
              onClick={(): void | Promise<void> => navigate(`/person/${person.id}/edit`)}
              title={intl.formatMessage({ id: 'button.edit' })}
            />
          ),
          authorizer.hasRole('ROLE_ADMIN') && (
            <Button
              icon={'pi pi-trash'}
              severity={'danger'}
              onClick={(): void =>
                deletePerson(personApi, toast, intl, navigate, person.id, displayName(getMainName(person)))
              }
              title={intl.formatMessage({ id: 'button.delete' })}
            />
          ),
          authorizer.hasRole('ROLE_EDITOR') && (
            <Button
              icon={'pi pi-trash'}
              severity={'warning'}
              onClick={(): void =>
                requestDeletePerson(personApi, toast, intl, navigate, person.id, displayName(getMainName(person)))
              }
              title={intl.formatMessage({ id: 'button.deleteRequest' })}
            />
          ),
        ],
      }}
      details={[
        renderBasicData(person),
        renderIdsData(person),
        renderAdditionalInformationData(person),
        renderLinkingData(person),
        renderAdminData(person),
      ]}
      tabs={[
        {
          title: intl.formatMessage({ id: 'entity.works' }),
          content: <SearchResultsWork externalParams={{ queryString: `person_id:${person.id}` }}></SearchResultsWork>,
        },
        {
          title: intl.formatMessage({ id: 'entity.organisations' }),
          content: (
            <SearchResultsOrganisation
              externalParams={{
                queryString: (person.personOrganisations || [{ organisation: { id: 0 } }])
                  .map((po) => `id:${po.organisation.id}`)
                  .join(' OR '),
              }}
            ></SearchResultsOrganisation>
          ),
        },
        {
          title: intl.formatMessage({ id: 'entity.projects' }),
          content: (
            <SearchResultsProject
              externalParams={{
                queryString: (person.projects || [{ id: 0 }])
                  .map((po) => `id:${po.id}`)
                  .join(' OR '),
              }}
            ></SearchResultsProject>
          ),
        },
      ]}
    />
  );
};

export default DetailPerson;
